import * as preact from "preact";
import PropTypes from "prop-types";

import { arrayRemove } from "../../foundation/utils/array";
import classname from "../../foundation/utils/classname";
import { cmdKey, ctrlOrWinKey } from "../../foundation/utils/keyboard";
import { stopPropagation } from "../../foundation/utils/legacy";
import noderef from "../../foundation/utils/noderef";
import { isMacOS } from "../../foundation/utils/ua-utils";

import IconChevron from "../Icons/IconChevron";
import NavigationDirection from "../Navigation/NavigationDirection";

import Node from "./Node";
import DragDropNode from "./Node/DragDropNode";

import "./style.css";

function collectNodes(nodeList, collapsedNodes, parentId) {
    return [].concat(...nodeList.map(node => {
        node.parent = parentId;

        if (collapsedNodes.includes(node.id)) {
            return node;
        }

        return [node].concat(collectNodes(node.children, collapsedNodes, node.id));
    }));
}

function findNode(nodeList, nodeId) {
    for (const node of nodeList) {
        if (node.id === nodeId) {
            return node;
        }

        const foundNode = findNode(node.children, nodeId);
        if (foundNode) {
            return foundNode;
        }
    }

    return null;
}

function isChild(parentNode, childNodeId) {
    return !!findNode(parentNode.children, childNodeId);
}

const SLIGHT_DELAY = 300;
const ALMOST_IMMEDIATELY = 4;

/**
 * TreeView component which displays a tree data structure in a nested list format.
 * It basically looks like the folder list view in Finder/File Explorer/Nautilus/Konqueror.
 *
 * The styles that can be customized can be provided by the following CSS variables:
 *  - `--node-color` (default: `var(--teflon)`): Changes the text color of a node.
 *  - `--node-color-selected` (default: `var(--whitey)`): Changes the text color of a selected node.
 *  - `--node-color-selected_blur` (default: `var(--cornflower-blue-40pc)`): Changes the text color of a selected node for a tree view which is out of focus.
 *  - `--node-bg-selected` (default: `transparent`): Changes the background color of a selected node.
 *  - `--node-bg-selected_blur` (default: `transparent`): Changes the background color of a selected node for a tree view which is out of focus.
 *  - `--node-chevron-color` (default: `var(--gandalf)`): Changes the chevron color.
 *  - `--node-chevron-color-selected` (default: `var(--node-chevron-color, var(--gandalf))`): Changes the chevron color of a selected node.
 *  - `--node-chevron-color-selected_blur` (default: `var(--node-chevron-color-selected, var(--node-chevron-color, var(--gandalf)))`): Changes the chevron color of a selected node for a tree view which is out of focus.
 */

class TreeView extends preact.Component { // implements Navigable
    mounted = false;

    constructor(props) {
        super(props);

        this.handleStartDrag = this.handleStartDrag.bind(this);
        this.handleChevronClick = this.handleChevronClick.bind(this);
        this.handleNodeSelect = this.handleNodeSelect.bind(this);
        this.handleNodeHover = this.handleNodeHover.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleFocus = this.handleFocus.bind(this);
        this.handleBlur = this.handleBlur.bind(this);

        const { selectedNodeId, tree } = props;

        this.nodeList = collectNodes(tree, [], null);

        this.state = {
            collapsedNodes: [],
            selectedNodeId
        };
    }

    gainNavigation = direction => {
        const { giveNavigation } = this.props;

        let nextNode;
        if (direction === NavigationDirection.Down) {
            nextNode = this.findNextSelectableNode(null, 0, true);
        } else if (direction === NavigationDirection.Up) {
            nextNode = this.findNextSelectableNode(null, this.nodeList.length - 1, false);
        }

        if (nextNode) {
            this.nodeRefs.base.focus({
                preventScroll: true
            });
            this.handleNodeSelect(nextNode.id);
        } else if (giveNavigation) {
            giveNavigation(direction, this);
        }
    }

    findSelectedNodeIndex() {
        const { selectedNodeId, collapsedNodes } = this.state;

        const nodeIndex = this.nodeList.findIndex(({ id }) => id === selectedNodeId);
        if (nodeIndex > -1) {
            return nodeIndex;
        }

        const collapsedSectionIndex = this.nodeList.findIndex(({ id, children }) =>
            collapsedNodes.includes(id) && this.getDescendantSectionIds({ id, children }).includes(selectedNodeId)
        );

        return collapsedSectionIndex;
    }

    findNextSelectableNode(ev, selectedNodeIndex, after) {
        if (selectedNodeIndex < 0 || selectedNodeIndex >= this.nodeList.length || !this.nodeList[selectedNodeIndex]) {
            return;
        }

        if (this.nodeList[selectedNodeIndex].nonSelectable) {
            let nextNodeIndex;
            if (after) {
                nextNodeIndex = isMacOS() && ev && ev.metaKey ? this.nodeList.length - 1 : selectedNodeIndex + 1;
            } else {
                nextNodeIndex = isMacOS() && ev && ev.metaKey ? 0 : selectedNodeIndex - 1;
            }

            return this.findNextSelectableNode(ev, nextNodeIndex, after);
        }

        return this.nodeList[selectedNodeIndex];
    }

    toggleExpand(nodeId) {
        this.nodeRefs.base.focus({
            preventScroll: true
        });

        this.setState(state => {
            const { collapsedNodes } = state;

            if (collapsedNodes.includes(nodeId)) {
                arrayRemove(collapsedNodes, id => id === nodeId);
            } else {
                collapsedNodes.push(nodeId);
            }

            return { collapsedNodes };
        });
    }

    getDescendantSectionIds = ({ id, children }) => {
        const result = [id];
        if (children) {
            children.forEach(child => {
                result.push(...this.getDescendantSectionIds(child));
            });
        }

        return result;
    };

    handleStartDrag(nodeId) {
        const { collapsedNodes } = this.state;
        if (!collapsedNodes.includes(nodeId)) {
            this.setState({
                collapsedNodes: [...collapsedNodes, nodeId]
            });
        }
    }

    handleChevronClick(nodeId) {
        const { tree } = this.props;
        const { selectedNodeId } = this.state;

        this.toggleExpand(nodeId);

        const clickedNode = findNode(tree, nodeId);
        if (isChild(clickedNode, selectedNodeId)) {
            this.handleNodeSelect(nodeId, { expandHandled: true });
        }
    }

    handleNodeSelect(nodeId, { debounce = false, isSelectedWithClick = false, expandHandled = false } = {}) {
        const { onNodeSelect, frozen } = this.props;
        const { nonSelectable, expandOnClick } = this.nodeList.find(({ id }) => id === nodeId);

        if (!expandHandled && expandOnClick) {
            this.toggleExpand(nodeId);
        }

        if (frozen || nonSelectable) {
            return;
        }

        this.setState({
            selectedNodeId: nodeId
        });

        const timeout = debounce ? SLIGHT_DELAY : ALMOST_IMMEDIATELY;

        clearTimeout(this.nodeTimeout);
        this.nodeTimeout = setTimeout(onNodeSelect, timeout, nodeId, isSelectedWithClick);
    }

    handleNodeHover(nodeId) {
        const { hoverable } = this.props;

        if (!hoverable) {
            return;
        }

        this.handleNodeSelect(nodeId);
    }

    handleArrowDown(ev) {
        const { giveNavigation } = this.props;
        const { selectedNodeId } = this.state;

        if (selectedNodeId === null && this.nodeList.length) {
            this.handleNodeSelect(this.nodeList[0].id, { debounce: true });
            return;
        }

        const nodeIndex = this.findSelectedNodeIndex();
        const nextNodeIndex = isMacOS() && ev.metaKey ? this.nodeList.length - 1 : nodeIndex + 1;
        const nextSelectableNode = this.findNextSelectableNode(ev, nextNodeIndex, true);

        if (nextSelectableNode) {
            this.handleNodeSelect(nextSelectableNode.id, { debounce: true });
        } else if (giveNavigation) {
            clearTimeout(this.nodeTimeout);
            giveNavigation(NavigationDirection.Down, this);
        }
    }

    handleArrowUp(ev) {
        const { giveNavigation } = this.props;
        const { selectedNodeId } = this.state;

        if (selectedNodeId === null && this.nodeList.length) {
            this.handleNodeSelect(this.nodeList[0].id, { debounce: true });
            return;
        }

        const nodeIndex = this.findSelectedNodeIndex();
        const previousNodeIndex = isMacOS() && ev.metaKey ? 0 : nodeIndex - 1;
        const nextSelectableNode = this.findNextSelectableNode(ev, previousNodeIndex, false);

        if (nextSelectableNode) {
            this.handleNodeSelect(nextSelectableNode.id, { debounce: true });
        } else if (giveNavigation) {
            clearTimeout(this.nodeTimeout);
            giveNavigation(NavigationDirection.Up, this);
        }
    }

    handleArrowLeft() {
        const { collapsedNodes } = this.state;
        const nodeIndex = this.findSelectedNodeIndex();
        if (nodeIndex === -1) {
            return;
        }

        const node = this.nodeList[nodeIndex];

        if ((!node.children.length || collapsedNodes.includes(node.id))) {
            if (node.parent !== null) {
                this.handleNodeSelect(node.parent, { debounce: true });
            }
        } else {
            this.handleChevronClick(node.id);
        }
    }

    handleArrowRight() {
        const { collapsedNodes } = this.state;
        const nodeIndex = this.findSelectedNodeIndex();
        if (nodeIndex === -1) {
            return;
        }

        const node = this.nodeList[nodeIndex];

        if (node.children.length && collapsedNodes.includes(node.id)) {
            this.handleChevronClick(node.id);
        }
    }

    handleHome() {
        const nodeIndex = this.findSelectedNodeIndex();
        if (nodeIndex < 1) {
            return;
        }

        const { id } = this.nodeList[0];

        this.handleNodeSelect(id, { debounce: true });
    }

    handleEnd() {
        const nodeIndex = this.findSelectedNodeIndex();
        if (nodeIndex === -1 || nodeIndex === this.nodeList.length - 1) {
            return;
        }

        const lastNode = this.nodeList[this.nodeList.length - 1];

        this.handleNodeSelect(lastNode.id, { debounce: true });
    }

    handleKeyDown(ev) {
        if (cmdKey(ev) || ev.altKey || ev.shiftKey || ctrlOrWinKey(ev)) {
            return;
        }

        let preventDefault = true;

        switch (ev.keyCode) {
            case Zeplin.KEY_CODE.ArrowDown:
                this.handleArrowDown(ev);
                break;

            case Zeplin.KEY_CODE.ArrowUp:
                this.handleArrowUp(ev);
                break;

            case Zeplin.KEY_CODE.ArrowLeft:
                this.handleArrowLeft();
                break;

            case Zeplin.KEY_CODE.ArrowRight:
                this.handleArrowRight();
                break;

            case Zeplin.KEY_CODE.HOME:
                this.handleHome();
                break;

            case Zeplin.KEY_CODE.END:
                this.handleEnd();
                break;

            default:
                preventDefault = false;
        }

        if (preventDefault) {
            ev.preventDefault();
        }
    }

    handleFocus() {
        document.addEventListener("keydown", this.handleKeyDown, true);
    }

    handleBlur() {
        document.removeEventListener("keydown", this.handleKeyDown, true);
    }

    componentDidMount() {
        this.mounted = true;

        const { forceFocus } = this.props;
        if (forceFocus) {
            this.nodeRefs.base.focus({
                preventScroll: true
            });
        }
    }

    componentWillUpdate(nextProps, nextState) {
        const { forceFocus } = this.props;
        const { collapsedNodes } = this.state;

        const { tree, selectedNodeId, forceFocus: nextForceFocus } = nextProps;
        const { collapsedNodes: nextCollapsedNodes } = nextState;

        if (collapsedNodes.length !== nextCollapsedNodes) {
            this.nodeList = collectNodes(tree, nextCollapsedNodes, null);
        }

        if (selectedNodeId !== this.props.selectedNodeId) {
            this.setState({ selectedNodeId });
        }

        if (!nextForceFocus && forceFocus && this.nodeRefs.base) {
            this.nodeRefs.base.blur();
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.selectedNodeId !== this.props.selectedNodeId && this.props.scrollIntoNode) {
            const itemToScrollInto = this.nodeRefs.base
                .querySelector(`[data-id="${this.props.selectedNodeId}"]`);

            if (itemToScrollInto) {
                itemToScrollInto.scrollIntoView();
            }
        }
    }

    componentWillUnmount() {
        this.mounted = false;

        document.removeEventListener("keydown", this.handleKeyDown);
    }

    renderTree(nodeList, level, parentIndex) {
        const {
            nodeClass,
            levelBasedNodeClasses,
            nodeChevronIcon,
            nodePadding: treeViewNodePadding,
            nodePaddingPerLevel,
            internalNodePadding,
            leafNodePadding,
            dragDropEnabled,
            onComponentToSectionNodeDrop,
            onReorderComponentSectionOrGroup,
            allowScrollOnMount,
            compareIdsForHighlight
        } = this.props;
        const { collapsedNodes, selectedNodeId } = this.state;

        return nodeList.map(({
            id, idPath, styleguideId, title, content, class: cssClass, nodePadding, children, disableNodeDragAndDrop
        }, index) => {
            const collapsed = collapsedNodes.includes(id);
            const selected = selectedNodeId === id;
            const highlighted = (compareIdsForHighlight && compareIdsForHighlight(id, selectedNodeId)) ||
                (collapsed && this.getDescendantSectionIds({ id, children }).includes(selectedNodeId));

            const TreeNode = dragDropEnabled && !disableNodeDragAndDrop ? DragDropNode : Node;
            const extraPadding = nodePadding ? nodePadding : treeViewNodePadding;

            const levelBasedNodeClass = levelBasedNodeClasses?.[level] ?? "";

            return (
                <div key={`tree-${id}`} class="tree" data-id={id}>
                    <TreeNode
                        key={`node-${id}`}
                        class={[nodeClass, cssClass, levelBasedNodeClass]}
                        chevronIcon={nodeChevronIcon}
                        extraPadding={extraPadding}
                        unitPadding={nodePaddingPerLevel}
                        internalNodePadding={internalNodePadding}
                        leafNodePadding={leafNodePadding}
                        id={id}
                        index={index + 1}
                        parentIndex={parentIndex}
                        idPath={idPath}
                        styleguideId={styleguideId}
                        title={title}
                        level={level}
                        numberOfChildren={children.length}
                        lastNode={nodeList.length === index + 1}
                        collapsed={collapsed}
                        selected={selected || highlighted}
                        allowScrollOnMount={allowScrollOnMount}
                        onChevronClick={this.handleChevronClick}
                        onNodeSelect={this.handleNodeSelect}
                        onNodeHover={this.handleNodeHover}
                        onStartDrag={this.handleStartDrag}
                        onComponentToSectionNodeDrop={onComponentToSectionNodeDrop}
                        onReorderComponentSectionOrGroup={onReorderComponentSectionOrGroup}>
                        {content}
                    </TreeNode>
                    {children.length !== 0 && !collapsed && this.renderTree(children, level + 1, index + 1)}
                </div>
            );
        });
    }

    render() {
        const { treeViewId, class: externalClass, tree, startingLevel = 1 } = this.props;
        const classes = classname("treeView", externalClass);

        return (
            <div
                id={treeViewId}
                class={classes}
                tabIndex="0"
                ref={noderef(this, "base")}
                onClick={stopPropagation}
                onFocus={this.handleFocus}
                onBlur={this.handleBlur}>
                {this.renderTree(tree, startingLevel, null)}
            </div>
        );
    }
}

const nodeShape = {
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    content: PropTypes.node.isRequired
};

const NodeShape = PropTypes.shape(nodeShape);
NodeShape.children = PropTypes.arrayOf(NodeShape).isRequired;

TreeView.propTypes = {
    /** CSS class(es) to be added to the root element. */
    class: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    /** External class name to be added to each node element. */
    nodeClass: PropTypes.string,
    /** The icon component to be used for the chevron in node */
    nodeChevronIcon: PropTypes.elementType,
    /** Extra left padding be added to each node element. */
    nodePadding: PropTypes.number,
    /** Has draggable and droppable nodes */
    dragDropEnabled: PropTypes.bool,
    /** The tree data structure to be rendered. */
    tree: PropTypes.arrayOf(NodeShape).isRequired,
    /** The id of the node that is selected in the tree if any. */
    selectedNodeId: PropTypes.string,
    /** Compares selectedNodeId with given node's id to show the node as selected or not */
    compareIdsForHighlight: PropTypes.func,
    /** Callback to be invoked when a node is selected via click, hover or keypress. */
    onNodeSelect: PropTypes.func.isRequired,
    /** Whether the view should be scrolled to make the selected node visible when it mounts
     *  Note: Currently scrolling is not available when dragDropEnabled is true */
    allowScrollOnMount: PropTypes.bool,
    /** Whether node items are selected when hover or not */
    hoverable: PropTypes.bool,
    /** Whether TreeView has focused or not */
    forceFocus: PropTypes.bool,
    /** Whether node items are selectable (via hover/click/keydown) or not */
    frozen: PropTypes.bool,
    treeViewId: PropTypes.string,
    scrollIntoNode: PropTypes.bool
};

TreeView.defaultProps = {
    nodeChevronIcon: IconChevron,
    allowScrollOnMount: true
};

export default TreeView;
